// react
import { MouseEvent, useContext } from 'react';

// types
import { Entity, ExpenseType } from '../../../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUser, ContextUserEntity } from '../../../../../../App.context';

// mui
import { Grid, Typography } from '@mui/material';

// ui
import { BtnBox, BtnBoxProps } from '../../../../../ui/BtnBox/BtnBox';
import { MenuItemProps } from '../../../../../ui/Menu/Menu';
import { TypographyOverflow } from '../../../../../ui/TypographyOverflow/TypographyOverflow';

// dialogs
import { DialogDeleteExpenseType } from '../../../../../dialogs/DialogDeleteExpenseType/DialogDeleteExpenseType';
import { DialogEditExpenseType } from '../../../../../dialogs/DialogEditExpenseType/DialogEditExpenseType';
import { DialogUpdateExpenseTypeStatus } from '../../../../../dialogs/DialogUpdateExpenseTypeStatus/DialogUpdateExpenseTypeStatus';

// icons
import {
	DeleteOutlined as IconDelete,
	DoDisturbOutlined as IconDoDisturb,
	ModeEditOutlined as IconModeEdit,
	PowerSettingsNewOutlined as IconPowerSettingsNew,
} from '@mui/icons-material';

// props
export interface ExpenseTypeProps {
	expenseType: ExpenseType;
	hasEntity?: boolean;
	isActive?: boolean;
	onClick: (e: MouseEvent<HTMLButtonElement>) => void;
	onSuccessDelete: () => void;
	onSuccessEdit: () => void;
	onSuccessUpdateStatus: () => void;
}

export const CardExpenseType = ({
	expenseType,
	hasEntity = false,
	isActive = false,
	onClick,
	onSuccessDelete,
	onSuccessEdit,
	onSuccessUpdateStatus,
}: ExpenseTypeProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogExpenseType = searchParams.get('dialogExpenseType');

	// vars
	const expenseTypeEntityId =
		expenseType.entity && typeof expenseType.entity === 'object'
			? expenseType.entity.id
			: expenseType.entity;
	const isDeactivated = expenseType.status === 'DEACTIVATED';
	const isDefault = !expenseType.entity;
	const isWfa = user?.role === 'WFA';

	const btnUpdateStatus: MenuItemProps = {
		children: t('deactivateExpenseType'),
		icon: <IconDoDisturb color="error" />,
		onClick: (e) => {
			e.stopPropagation();

			let statusNew = 'DEACTIVATED';

			if (isDeactivated) {
				statusNew = 'ACTIVE';
			}

			searchParams.set('dialog', 'updateExpenseTypeStatus');
			searchParams.set('dialogExpenseType', String(expenseType.id));
			searchParams.set('dialogStatus', statusNew);
			setSearchParams(searchParams);
		},
	};

	if (isDeactivated) {
		btnUpdateStatus.children = t('activateExpenseType');
		btnUpdateStatus.icon = <IconPowerSettingsNew color="success" />;
	}

	let isEditable = false;

	if ((isWfa && !expenseType.entity) || expenseTypeEntityId === userEntity?.id) {
		isEditable = true;
	}

	const BtnBoxProps: BtnBoxProps = {
		children: (
			<Grid container>
				<Grid item xs={12}>
					<Typography fontWeight={600}>{expenseType.name}</Typography>
				</Grid>
				{hasEntity && expenseType.entity && (
					<Grid item xs={12}>
						<TypographyOverflow
							color={isActive ? 'brand.blue.300' : 'brand.grey.500'}
							variant="caption">
							{(expenseType.entity as Entity).name}
						</TypographyOverflow>
					</Grid>
				)}
			</Grid>
		),
		count: expenseType.documentTypes.length,
		isActive,
		onClick,
	};

	if (isDeactivated) {
		BtnBoxProps.ChipStatusProps = {
			status: expenseType.status,
		};
	} else if (isDefault) {
		BtnBoxProps.ChipProps = {
			color: 'info',
			label: 'World Bank',
			size: 'small',
			variant: 'twoTone',
		};
	}

	if (isEditable) {
		BtnBoxProps.BtnMenuProps = {
			items: [
				{
					children: t('editExpenseType'),
					icon: <IconModeEdit />,
					onClick: (e) => {
						e.stopPropagation();

						searchParams.set('dialog', 'editExpenseType');
						searchParams.set('dialogExpenseType', String(expenseType.id));
						setSearchParams(searchParams);
					},
				},
				btnUpdateStatus,
				{
					children: t('deleteExpenseType'),
					disabled: true,
					icon: <IconDelete color="error" />,
					onClick: (e) => {
						e.stopPropagation();

						searchParams.set('dialog', 'deleteExpenseType');
						searchParams.set('dialogExpenseType', String(expenseType.id));
						setSearchParams(searchParams);
					},
				},
			],
		};
	}

	return (
		<>
			<BtnBox {...BtnBoxProps} />
			<DialogDeleteExpenseType
				isOpen={
					paramDialog === 'deleteExpenseType' && paramDialogExpenseType === String(expenseType.id)
				}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogExpenseType');
					setSearchParams(searchParams);
				}}
				onSuccess={onSuccessDelete}
			/>
			<DialogEditExpenseType
				isOpen={
					paramDialog === 'editExpenseType' && paramDialogExpenseType === String(expenseType.id)
				}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogExpenseType');
					setSearchParams(searchParams);
				}}
				onSuccess={onSuccessEdit}
			/>
			<DialogUpdateExpenseTypeStatus
				isOpen={
					paramDialog === 'updateExpenseTypeStatus' &&
					paramDialogExpenseType === String(expenseType.id)
				}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogExpenseType');
					searchParams.delete('dialogStatus');
					setSearchParams(searchParams);
				}}
				onSuccess={onSuccessUpdateStatus}
			/>
		</>
	);
};
